import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
	CreateRbidosRequest,
	InnDataModel,
	RemoteDocumentModel,
	RbidosRequest,
	RestartRbidosRequestModel,
	documentSlice,
	DocumentsNS,
	MccCodeType,
	MccGroupType,
	CopyRbidosRequestModel,
	PanelRadioOptions,
} from 'Pages/registratorCabinet'
import { AddressSuggest, RequestParams, ResponseCatalog } from 'Models'
import { Callback } from 'Src/types/utilTypes'
import { RbidosLeadCreateModel, RbidosLeadModel, AFTER_SBER_ID_USER_INFO } from 'Pages/lead'

export type OrderEditState = {
	editRbidosRequest: RbidosRequest | null
	lead: RbidosLeadModel
	socketIds: number[]
	socketRequest: RbidosRequest | null
	documentsUpdated: boolean
	sberIdInfo: AFTER_SBER_ID_USER_INFO & { dadataInfo: AddressSuggest }
	mccCodes: MccCodeType[]
	mccGroup: MccGroupType[]
	sbolType: PanelRadioOptions
}

export const orderEditInitialState: OrderEditState = {
	editRbidosRequest: null,
	lead: null,
	socketIds: [],
	socketRequest: null,
	documentsUpdated: false,
	sberIdInfo: null,
	mccCodes: [],
	mccGroup: [],
	sbolType: null,
}

export namespace OrderEditNS {
	export type CreateRbidosRequestAction = PayloadAction<{ data: CreateRbidosRequest }>
	export type CreateRbidosLeadAction = PayloadAction<{
		data: RbidosLeadCreateModel
		callback?: (lead: RbidosLeadModel) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
	}>
	export type SetRbidosLead = PayloadAction<RbidosLeadModel>
	export type GetEditRbidosRequest = PayloadAction<{ id: number | string }>
	export type SetEditRbidosRequest = PayloadAction<RbidosRequest>
	export type UpdateEditRbidosRequest = PayloadAction<{
		id: number | string
		data: RbidosRequest
		callback?: (res: RbidosRequest) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
	}>
	export type PushEditRbidosRequest = PayloadAction<{
		id: number | string
		data: RbidosRequest
		callback?: (res: RbidosRequest, isQrCodeForSbol?: boolean) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
		isQrCodeForSbol?: boolean
	}>
	export type rollbackStatusRbidosRequest = PayloadAction<{
		id: number | string
		callback?: (res: RbidosRequest) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
	}>
	export type GetInnRbidosRequest = PayloadAction<{
		data: InnDataModel
		callback?: (inn: string | number) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
	}>
	export type SetInnRbidosRequest = PayloadAction<string | number>
	export type SendCodeEmailRbidosRequest = PayloadAction<{
		id: number | string
		data: RbidosRequest
		flagAgain: boolean
		callback?: (res: RbidosRequest) => unknown
		errorCallback?: (errors: Record<string, string>) => unknown
	}>
	export type sendRemoteDocuments = PayloadAction<{
		data: RemoteDocumentModel
		callback: Callback
	}>
	export type SaveRequest = PayloadAction<{
		id: number
		data: CopyRbidosRequestModel | {}
	}>

	export type pushSocketId = PayloadAction<{ id: number }>
	export type clearSocketId = PayloadAction<{ id: number }>
	export type getSocket = PayloadAction<{ id: number }>
	export type setSocket = PayloadAction<{ data: RbidosRequest }>
	export type updateSocketId = PayloadAction<{ id: number }>
	export type setSocketRequest = PayloadAction<RbidosRequest>
	export type setDocumentsUpdate = PayloadAction<boolean>

	export type getSberIdInfo = PayloadAction<number>
	export type setSberIdInfo = PayloadAction<
		AFTER_SBER_ID_USER_INFO & { dadataInfo: AddressSuggest }
	>
	export type clearSberIdInfo = PayloadAction
	export type restartRbidosRequest = PayloadAction<{ data: RestartRbidosRequestModel }>
	export type DeleteRbidosRequest = PayloadAction<{ id: number; callback?: Callback }>
	export type RestoreRbidosRequest = PayloadAction<{ id: number; callback?: Callback }>
	export type SendStatusPaymentCallback = PayloadAction<{
		data: { orderId: string }
	}>
	export type GetMccCodes = PayloadAction<{
		requestParams?: RequestParams
		callback?: Callback
	}>
	export type SetMccCodes = PayloadAction<ResponseCatalog<MccCodeType>>
	export type GetMccGroups = PayloadAction<{
		requestParams?: RequestParams
		callback?: Callback
	}>
	export type SetMccGroups = PayloadAction<ResponseCatalog<MccGroupType>>
	export type GetRbidosLeads = PayloadAction<{
		params: RequestParams
		callback?: Callback
	}>
	export type SetSbolType = PayloadAction<PanelRadioOptions>
	export type ClearSbolType = PayloadAction
}

export const orderEditSlice = createSlice({
	name: 'orderEdit',
	initialState: orderEditInitialState,
	reducers: {
		createOne: (state, action: OrderEditNS.CreateRbidosRequestAction) => {},
		getOne: (state, action: OrderEditNS.GetEditRbidosRequest) => {},
		setOne: (state, action: OrderEditNS.SetEditRbidosRequest) => {
			state.editRbidosRequest = action.payload
		},
		updateOne: (state, action: OrderEditNS.UpdateEditRbidosRequest) => {},
		pushRbidosRequest: (state, action: OrderEditNS.PushEditRbidosRequest) => {},
		clearOne: (state) => {
			state.editRbidosRequest = null
		},
		createRbidosLead: (state, action: OrderEditNS.CreateRbidosLeadAction) => {},
		setEditRbidosLead: (state, action: OrderEditNS.SetRbidosLead) => {
			state.lead = action.payload
		},
		rollbackStatusRbidosRequest: (state, action: OrderEditNS.rollbackStatusRbidosRequest) => {},
		getInnRbidosRequest: (state, action: OrderEditNS.GetInnRbidosRequest) => {},
		sendCodeEmailRbidosRequest: (state, action: OrderEditNS.SendCodeEmailRbidosRequest) => {},
		sendRemoteDocuments: (state, action: OrderEditNS.sendRemoteDocuments) => {},
		saveRequest: (state, action: OrderEditNS.SaveRequest) => {},
		pushSocketId: (state, action: OrderEditNS.pushSocketId) => {
			state.socketIds = [...state.socketIds, action.payload.id]
		},
		clearSocketId: (state, action: OrderEditNS.clearSocketId) => {
			state.socketIds = state.socketIds.filter((id) => id !== action.payload.id)
		},
		getSocket: (state, action: OrderEditNS.getSocket) => {},
		updateSocketId: (state, action: OrderEditNS.updateSocketId) => {},
		setSocketRequest: (state, action: OrderEditNS.setSocketRequest) => {
			state.socketRequest = action.payload
		},
		setDocumentsUpdate: (state, action: OrderEditNS.setDocumentsUpdate) => {
			state.documentsUpdated = action.payload
		},

		getSberIdInfo: (state, action: OrderEditNS.getSberIdInfo) => {},
		setSberIdInfo: (state, action: OrderEditNS.setSberIdInfo) => {
			state.sberIdInfo = action.payload
		},
		clearSberIdInfo: (state, action: OrderEditNS.clearSberIdInfo) => {
			state.sberIdInfo = null
		},
		restartRbidosRequest: (state, action: OrderEditNS.restartRbidosRequest) => {},
		deleteRbidosRequest: (state, action: OrderEditNS.DeleteRbidosRequest) => {},
		restoreRbidosRequest: (state, action: OrderEditNS.RestoreRbidosRequest) => {},
		sendStatusPaymentCallback: (state, action: OrderEditNS.SendStatusPaymentCallback) => {},
		getMccCodes: (state, action: OrderEditNS.GetMccCodes) => {},
		setMccCodes: (state, action: OrderEditNS.SetMccCodes) => {
			state.mccCodes = action.payload.entries
		},
		getMccGroups: (state, action: OrderEditNS.GetMccGroups) => {},
		setMccGroups: (state, action: OrderEditNS.SetMccGroups) => {
			state.mccGroup = action.payload.entries
		},
		getRbidosLeads: (state, action: OrderEditNS.GetRbidosLeads) => {},
		setSbolType: (state, action: OrderEditNS.SetSbolType) => {
			state.sbolType = action.payload
		},
		clearSbolType: (state, action: OrderEditNS.ClearSbolType) => {
			state.sbolType = null
		},
	},
	extraReducers: {
		[documentSlice.actions.setOne.type]: (state, action: DocumentsNS.SetDocument) => {
			const { nameGroup, document } = action.payload
			state.editRbidosRequest.user_docs[nameGroup].documents = document ? [document] : []
		},
	},
})
